import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';

import MediaPreview from 'components/MediaPreview';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';

import CcmCampaignEditModal from 'modules/campaigns/components/ccm/components/modals/edit/CcmCampaignEditModal';
import CcmCampaignMetadataModal from 'modules/campaigns/components/ccm/components/modals/metadata/CcmCampaignMetadataModal';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import {
  isGoLiveDateReached,
  isInfluencerSignInDateReached
} from 'modules/campaigns/components/ccm/utils/ccm-timeline';

import {
  prepareCampaignAssetForMediaPreview,
  prepareCampaignAssetsForGallery
} from 'utils/campaignAsset';

import './ccm-campaign-overview.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./ccm-campaign-overview.i18n.json'));

import type { Campaign } from 'types';
import CcmCampaignMainInfo from './CcmCampaignMainInfo';

type OwnProps = {
  campaign: Campaign | undefined,
  isCcmModerator: boolean
};

type StateProps = {
  currentOrganizationId: number
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'ccm_campaign_overview';

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

const ActionButtonWithEditModal = withModal(ActionButton, {
  modalContent: CcmCampaignEditModal
});

const ActionButtonWithMetadataModal = withModal(ActionButton, {
  modalContent: CcmCampaignMetadataModal,
  width: 'auto'
});

const CcmCampaignOverview: React.FC<Props> = ({
  campaign,
  currentOrganizationId,
  isCcmModerator
}): JSX.Element => {
  const hasMetadata = isString(campaign?.salesCockpitNcode)
    && isString(campaign?.creativeFactoryInsertionOrderId);

  const renderInspirationalMaterial = (): React.ReactNode => {
    const galleryItems = prepareCampaignAssetsForGallery(campaign?.assets || []);

    return (
      <div className="ccm-campaign-overview__inspirational-material">
        {
          (campaign?.assets || []).map((asset, index) => (
            <DivWithModal
              key={`ccm-campaign-overview__inspirational-material--${asset.id}`}
              modalContentProps={{
                items: galleryItems,
                currentPosition: index
              }}
            >
              <MediaPreview
                attachment={prepareCampaignAssetForMediaPreview(asset)}
                width={133}
                height={133}
              />
            </DivWithModal>
          ))
        }
      </div>
    );
  };

  const renderMetadataModalButton = (): React.ReactNode => (
    <ActionButtonWithMetadataModal
      disabled={hasMetadata || !isGoLiveDateReached(campaign?.timeline)}
      modalContentProps={{
        campaignId: campaign?.id
      }}
    >
      {__t(`${MSG_KEY}.buttons.metadata`)}
    </ActionButtonWithMetadataModal>
  );

  const renderButtons = (): React.ReactNode => {
    const MetadataModalButton = hasMetadata
      ? (
        <Tooltip
          message={__t(`${MSG_KEY}.tooltips.metadata_entered`)}
          width="normal"
          classNameWrapper="ccm-campaign-overview__button-tooltip"
        >
          {renderMetadataModalButton()}
        </Tooltip>
      ) : renderMetadataModalButton();

    return (
      <div className="ccm-campaign-overview__buttons">
        <div className="ccm-campaign-overview__metadata-button">
          {MetadataModalButton}
        </div>
        <div className="ccm-campaign-overview__edit-button">
          <ActionButtonWithEditModal
            disabled={isInfluencerSignInDateReached(campaign?.timeline)}
            modalContentProps={{
              campaignId: campaign?.id,
              organizationId: currentOrganizationId,
              brandProfileId: campaign?.brandProfile?.id
            }}
          >
            {__t(`${MSG_KEY}.buttons.edit`)}
          </ActionButtonWithEditModal>
        </div>
      </div>
    );
  };

  return (
    <div className="ccm-campaign-overview">
      <div className="ccm-campaign-overview__title">
        <div>
          {__t(`${MSG_KEY}.campaign_description`)}
        </div>
        {isCcmModerator && campaign?.campaignType === 'ccm' && renderButtons()}
      </div>
      <div className="ccm-campaign-overview__body">
        <CcmCampaignMainInfo
          campaign={campaign}
        />
        <div>
          <div className="ccm-campaign-overview__sections">
            <div className="ccm-campaign-overview__section">
              <div className="ccm-campaign-overview__section-title">
                {__t(`${MSG_KEY}.section_titles.campaign_brief`)}
              </div>
              <div className="ccm-campaign-overview__section-value">
                {campaign?.description}
              </div>
            </div>
            {
              !isEmpty(campaign?.assets) && (
                <div className="ccm-campaign-overview__section">
                  <div className="ccm-campaign-overview__section-title">
                    {__t(`${MSG_KEY}.section_titles.inspirational_material`)}
                  </div>
                  <div className="ccm-campaign-overview__section-value">
                    {renderInspirationalMaterial()}
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ organizations }): StateProps => ({
  currentOrganizationId: organizations.currentOrganizationId
});

export default connect(mapStateToProps)(CcmCampaignOverview);
